import { useCallback, useLayoutEffect, useRef, useState } from "react";
import type { FooterDensity } from "../FooterButton";

// Вътрешният отстъп на лентата от двете страни (`px-5`).
const BAR_PADDING_PX = 20;
// Между нещото вляво (часовника) и бутоните.
const LEADING_GAP_PX = 16;

/** Разстоянието между бутоните на всяка степен на свиване. */
export const FOOTER_ROW_GAP_CLASS: Record<FooterDensity, string> = {
  normal: "gap-2.5",
  compact: "gap-2",
  tight: "gap-1.5",
  squeeze: "gap-1.5",
};

/**
 * Колко да се свият бутоните в лентата под масите, за да се съберат ВСИЧКИ —
 * ограничение за броя няма. `FooterMeasureRow` рисува невидимо целия ред на
 * степените normal → compact → tight; избира се първата, която се събира в
 * лентата. Не се събира ли и tight — squeeze: бутоните се свиват заедно
 * (flex-shrink) и текстът им се реже с „…".
 *
 * Има ли нещо вляво в лентата (`leadingRef` — часовникът в режим „Схема"),
 * ширината му се запазва ОТ ДВЕТЕ страни (`sideInset`), за да останат бутоните
 * в средата, без да минават под него.
 */
export const useFooterDensity = (enabled: boolean) => {
  const barRef = useRef<HTMLDivElement | null>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const leadingRef = useRef<HTMLDivElement>(null);
  const [density, setDensity] = useState<FooterDensity>("normal");
  const [sideInset, setSideInset] = useState(BAR_PADDING_PX);

  const measure = useCallback(() => {
    const bar = barRef.current;
    const measureBox = measureRef.current;
    if (!bar || !measureBox) return;
    const leadingWidth = leadingRef.current?.offsetWidth ?? 0;
    const inset = leadingWidth > 0
      ? BAR_PADDING_PX + leadingWidth + LEADING_GAP_PX
      : BAR_PADDING_PX;
    setSideInset((previous) => (previous === inset ? previous : inset));
    const availableWidth = bar.clientWidth - 2 * inset;
    let next: FooterDensity = "squeeze";
    for (const row of measureBox.querySelectorAll<HTMLElement>(
      "[data-density]",
    )) {
      if (row.offsetWidth <= availableWidth) {
        next = row.dataset.density as FooterDensity;
        break;
      }
    }
    setDensity((previous) => (previous === next ? previous : next));
  }, []);

  // След всеки рендер — редът се сменя при влачене, смяна на езика и правата;
  // setState-ът е само при реална разлика, затова няма цикъл.
  useLayoutEffect(() => {
    if (enabled) measure();
  });

  // Смяна на размера на прозореца и късно заредени шрифтове.
  useLayoutEffect(() => {
    if (!enabled || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => measure());
    if (barRef.current) observer.observe(barRef.current);
    if (leadingRef.current) observer.observe(leadingRef.current);
    measureRef.current
      ?.querySelectorAll("[data-density]")
      .forEach((row) => observer.observe(row));
    return () => observer.disconnect();
  }, [enabled, measure]);

  return {
    density: enabled ? density : "normal",
    barRef,
    measureRef,
    leadingRef,
    sideInset: enabled ? sideInset : BAR_PADDING_PX,
  };
};
